/**
 * Console session.
 *
 * The credential is an httpOnly cookie the browser holds, so this only caches
 * *who* is signed in. `GET /platform/auth/me` on mount is what establishes that
 * — a page refresh must not drop the session, and there is no token to read.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import api, { setUnauthorizedHandler } from '../lib/api';
import type { PlatformAdmin } from '../lib/types';

interface AuthValue {
  admin: PlatformAdmin | null;
  /** True until the initial `me` check settles — routes must wait for it. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<PlatformAdmin | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/platform/auth/me');
      setAdmin(data.data);
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Any 401 from elsewhere in the app means the cookie expired mid-session.
  useEffect(() => {
    setUnauthorizedHandler(() => setAdmin(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post('/platform/auth/login', { email, password });
    setAdmin(data.data);
  }, []);

  const logout = useCallback(async () => {
    // Clear locally even if the call fails — the user asked to be signed out.
    try {
      await api.post('/platform/auth/logout');
    } finally {
      setAdmin(null);
    }
  }, []);

  const value = useMemo(
    () => ({ admin, loading, login, logout, refresh }),
    [admin, loading, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside an AuthProvider');
  return value;
}
